// TenantLockout — full-screen explainer when the active tenant is in a
// non-operating status (SUSPENDED / ARCHIVED). The auth hook (mig 241)
// stops emitting `active_tenant_id` for these statuses, so RLS blocks
// every read. Without this page the user would see an empty broken UI;
// with it they get a clear "your account is suspended" message and a
// path forward.
//
// Status semantics:
//   SUSPENDED — usually billing failure or admin-temp-suspended; reactivable
//   ARCHIVED  — off-boarded; meant to be permanent

import { LogOut, Pause, Archive, AlertTriangle, Mail } from 'lucide-react'
import { signOutEverywhere } from '@/lib/offlineSession'
import { useQueryClient } from '@tanstack/react-query'
import type { TenantStatus } from '@/lib/useActiveTenant'
import { PLATFORM_BRAND } from '@/lib/brand'

export default function TenantLockout({ status }: { status: TenantStatus }) {
  const qc = useQueryClient()
  const isSuspended = status === 'SUSPENDED'

  async function signOut() {
    await signOutEverywhere()
    qc.clear()
  }

  return (
    <div className="min-h-full flex items-center justify-center surface-sunk p-4">
      <div className="w-full max-w-md text-center">
        <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
          isSuspended ? 'bg-amber-900/40 text-amber-400' : 'bg-red-900/40 text-red-400'
        }`}>
          {isSuspended ? <Pause size={28} /> : <Archive size={28} />}
        </div>
        <h1 className="text-xl font-bold text-fg mb-1">
          {isSuspended ? 'Account suspended' : 'Account archived'}
        </h1>
        <p className="text-sm text-fg-muted mb-6">
          {isSuspended
            ? 'Your packhouse account is currently suspended. Sign-in works, but data access is paused until the suspension is cleared.'
            : 'Your packhouse account has been archived. Data is preserved, but access is no longer available.'}
        </p>

        <div className="card p-4 text-left space-y-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-fg-muted" />
            <p className="text-xs text-fg-secondary">
              Common reasons:
            </p>
          </div>
          <ul className="text-xs text-fg-muted list-disc pl-7 space-y-1">
            {isSuspended ? (
              <>
                <li>Outstanding subscription invoice</li>
                <li>Trial period ended without conversion</li>
                <li>Account flagged for review</li>
              </>
            ) : (
              <>
                <li>Subscription was cancelled and offboarding completed</li>
                <li>Account was archived after extended inactivity</li>
              </>
            )}
          </ul>
          <div className="flex items-start gap-2 pt-2 border-t hairline-strong">
            <Mail size={16} className="shrink-0 mt-0.5 text-fg-muted" />
            <p className="text-xs text-fg-secondary">
              Contact <a href={`mailto:${PLATFORM_BRAND.supportEmail}`} className="text-blue-400 hover:underline">{PLATFORM_BRAND.supportEmail}</a> to {isSuspended ? 'reactivate' : 'recover access or export your data'}.
            </p>
          </div>
          {isSuspended && (
            <p className="text-xs text-fg-muted pt-2 border-t hairline-strong">
              Already paid? Access is restored automatically once the payment clears —
              sign out and back in to pick up the unlock immediately.
            </p>
          )}
        </div>

        <button onClick={signOut} className="btn-secondary w-full flex items-center justify-center gap-1.5">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  )
}
